/**
 * Adding and removing several students from a custom group at once, and
 * the message a teacher sees when some of it fails.
 *
 * The API takes one student per request, so a bulk change can partly
 * succeed. Every failure is reported: nothing here retries or hides one.
 */
import { isForbidden, organisationErrorMessage } from '../../organisations/lib/errorMessage';

const FORBIDDEN_MESSAGE = 'You cannot change this group. It may have been deleted, or you may no longer have access to it.';

/** Runs `change(groupId, userId)` for every id. Resolves to a message
 * describing any failures, or null when all succeeded. */
export async function changeMembers(change, groupId, userIds) {
  const results = await Promise.allSettled(userIds.map((userId) => change(groupId, userId)));
  const failures = results.filter((r) => r.status === 'rejected').map((r) => r.reason);
  if (failures.length === 0) return null;
  if (failures.some(isForbidden)) return FORBIDDEN_MESSAGE;
  const detail = organisationErrorMessage(failures[0], 'Please try again.');
  return `${failures.length} of ${userIds.length} students could not be changed. ${detail}`;
}

/** The message for a failed create or delete of a group. */
export function groupErrorMessage(err, fallback) {
  return isForbidden(err) ? FORBIDDEN_MESSAGE : organisationErrorMessage(err, fallback);
}
